import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogOut, Users, Package, Calendar, FileText, Settings, Shield, Headphones } from 'lucide-react';
import { removeAdminToken, getAdminUser } from '../utils/auth';
import { supportService } from '../services/supportService';

const DashboardPage = () => {
  const navigate = useNavigate();
  const adminUser = getAdminUser();
  const [supportStats, setSupportStats] = useState({
    total_tickets: 0,
    unassigned_tickets: 0,
    status_counts: {}
  });

  useEffect(() => {
    // 🚫 Support statistics disabled - Support module is currently hidden
    // Uncomment below to re-enable when ENABLE_SUPPORT_MODULE = true
    
    /* 
    // Fetch support statistics for notifications (non-critical, don't fail login)
    const fetchSupportStats = async () => {
      try {
        const stats = await supportService.getSupportStats();
        setSupportStats(stats);
      } catch (error) {
        console.log('Could not load support statistics (non-critical):', error.message);
        // Don't propagate the error - support stats are not critical for dashboard functionality
      }
    };

    // Add a small delay to let authentication settle
    const timer = setTimeout(() => {
      fetchSupportStats();
    }, 500);

    return () => clearTimeout(timer);
    */
  }, []);

  const handleLogout = () => {
    removeAdminToken();
    navigate('/login');
  };

  // 🔧 FEATURE FLAGS - Enable/Disable modules
  const ENABLE_SUPPORT_MODULE = false; // Set to true to re-enable Support Management

  const menuItems = [
    { 
      icon: Users, 
      label: 'User Management', 
      path: '/users', 
      description: 'View and manage all registered users',
      color: 'bg-blue-50 text-blue-600'
    },
    { 
      icon: Package, 
      label: 'Asset Management', 
      path: '/assets', 
      description: 'Manage product listings and inventory',
      color: 'bg-purple-50 text-purple-600'
    },
    { 
      icon: Package, 
      label: 'Curation Packages', 
      path: '/curation', 
      description: 'Manage 360-day curation packages',
      color: 'bg-amber-50 text-amber-600'
    },
    { 
      icon: FileText, 
      label: 'FAQ Management', 
      path: '/faqs', 
      description: 'Create and manage frequently asked questions',
      color: 'bg-pink-50 text-pink-600'
    },
    { 
      icon: Settings, 
      label: 'Asset Status', 
      path: '/asset-status', 
      description: 'Update deployment and availability status',
      color: 'bg-green-50 text-green-600'
    },
    { 
      icon: Calendar, 
      label: 'Scheduling', 
      path: '/schedule', 
      description: 'View and manage user bookings',
      color: 'bg-orange-50 text-orange-600'
    },
    { 
      icon: Shield, 
      label: 'Super Admin Den', 
      path: '/super-admin-den', 
      description: 'Exclusive super admin management area',
      color: 'bg-red-50 text-red-600'
    },
    // 🚫 SUPPORT MODULE - TEMPORARILY DISABLED
    // To re-enable: Set ENABLE_SUPPORT_MODULE = true above
    ...(ENABLE_SUPPORT_MODULE ? [{
      icon: Headphones, 
      label: 'Support', 
      path: '/support', 
      description: 'Customer support and helpdesk management',
      color: 'bg-teal-50 text-teal-600',
      badge: supportStats.unassigned_tickets > 0 ? supportStats.unassigned_tickets : null
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex justify-between items-center">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Portal</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
              Welcome back, <span className="font-medium text-gray-900">{adminUser?.first_name || 'Admin'}</span>
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200 shrink-0"
          >
            <LogOut size={16} className="sm:w-4 sm:h-4 w-3.5 h-3.5" />
            <span className="hidden sm:inline">Logout</span>
            <span className="sm:hidden">Out</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="mb-6 sm:mb-10">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Management Dashboard</h2>
          <p className="text-sm sm:text-base text-gray-600">Select a module below to manage platform operations</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menuItems.map((item, index) => (
            <Card
              key={index}
              className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-200"
              onClick={() => navigate(item.path)}
            >
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${item.color}`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-semibold text-gray-900">{item.label}</CardTitle>
                      {item.badge && (
                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600 leading-relaxed">
                  {item.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;